/**
 * Geolocation permission page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { useLocationStore } from '../store';
import { toast } from 'sonner';

export default function Geolocation() {
  const navigate = useNavigate();
  const { setUserLocation, setLocationError } = useLocationStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleEnableLocation = () => {
    if (!navigator.geolocation) {
      setError('Tu navegador no soporta geolocalización');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        toast.success('Ubicación detectada');
        navigate('/scan');
      },
      (err) => {
        setLoading(false);
        let message = 'Error al obtener ubicación';
        
        switch (err.code) {
          case err.PERMISSION_DENIED:
            message = 'Permiso de ubicación denegado';
            break;
          case err.POSITION_UNAVAILABLE:
            message = 'Ubicación no disponible';
            break;
          case err.TIMEOUT:
            message = 'Tiempo de espera agotado';
            break;
        }
        
        setError(message);
        setLocationError(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };
  
  const handleSkip = () => {
    toast.info('Puedes habilitar la ubicación más tarde');
    navigate('/scan');
  };
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm text-center"
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-8"
        >
          <MapPin className="w-12 h-12 text-primary" />
        </motion.div>
        
        {/* Title */}
        <h1 className="font-display font-bold text-3xl mb-4">
          Habilitar ubicación
        </h1>
        
        <p className="text-muted-foreground mb-8">
          Necesitamos tu ubicación para verificar que estás cerca del club donde quieres jugar.
        </p>
        
        {/* Error message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg mb-6 text-destructive text-sm"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
        
        {/* Buttons */}
        <div className="space-y-3">
          <Button
            className="w-full h-14 text-lg font-bold uppercase tracking-wider glow-primary"
            onClick={handleEnableLocation}
            disabled={loading}
            data-testid="enable-location-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Habilitar ubicación
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            className="w-full h-12 text-muted-foreground"
            onClick={handleSkip}
            data-testid="skip-location-btn"
          >
            Continuar sin ubicación
          </Button>
        </div>
        
        {/* Info */}
        <p className="text-xs text-muted-foreground mt-6">
          Tu ubicación solo se usa para verificar proximidad al club.
          No almacenamos tu historial de ubicaciones.
        </p>
      </motion.div>
      </div>
    </div>
  );
}
